import { Bezier } from 'bezier-js';

import { Config } from '../video-data/config';
import type { Interpolation } from '../types';
import { assertObject, assertHasOwnProperties, assertInArray } from '../validators';
import type {
    EndPoint,
    Anchor,
    Position,
    CurvePointDescription,
    RemoveAnchoredPoints,
} from './Curve';
import { TOLERANCE, ControlPoint, Curve } from './Curve';
import { clamp } from './other';

const MIN_X = TOLERANCE;
const MAX_X = 1.0 - TOLERANCE;
const MAX_SCALE_DOWN_RECURSION = 10;
const SCALE_DOWN_FACTOR = 0.75;

function snapAxisComponent(value: number): number {
    if (Math.abs(value) < TOLERANCE) return 0;
    return clamp(value, -1, 1);
}

type InterpolationPreset = {
    interpolation: Interpolation;
    delay: number;
    duration: number;
};

export type BezierCurveDescription = {
    duration?: number;
    classType: 'BezierCurve';
    points: PointDescription[];
    removeAnchoredPoints?: RemoveAnchoredPoints;
};

type TangentDescription = {
    distance: number;
    angle: number;
};

type PointDescription = CurvePointDescription & {
    in?: TangentDescription;
    out?: TangentDescription;
};

export class BezierCurve extends Curve {
    override set percentage(value) {
        this._percentage = clamp(value + this.percentageOffset, 0, 1);
        this._value = this._evalPercentage(this._percentage);
    }

    override get percentage() {
        return this._percentage;
    }

    _createControlPoint(pos: Position) {
        return new ControlPoint(pos);
    }

    _addControlPoint(pos: Position) {
        const p = this._createControlPoint(pos);
        this._points.push(p);

        return p;
    }

    _getDistance(p1: Position, p2: Position) {
        return Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
    }

    _getAngle(p1: Position, p2: Position) {
        return Math.atan2(p2.y - p1.y, p2.x - p1.x);
    }

    _applyAngle(p1: Position, a: number) {
        return {
            x: p1.x * snapAxisComponent(Math.cos(a)),
            y: p1.y * snapAxisComponent(Math.sin(a)),
        };
    }

    _getEndPointIndexForPercentage(value: number): number {
        // TODO: this logic counts on all our point data to be within the 0 to 1 range. Since this isn't always the case
        //  we should update this logic as we currently capture our miss calculations through a QC step.
        const endPoints = this.getEndPoints();
        const nEndPoints = endPoints.length;
        let index = 0;

        if (value <= 0.0) {
            index = 0;
        } else if (value >= 1.0) {
            if (nEndPoints === 2) {
                index = 0;
            } else {
                index = this.points.length - 4;
            }
        } else {
            if (value < endPoints[0].x) {
                index = 0;
            } else if (value > endPoints[nEndPoints - 1].x) {
                index = (nEndPoints - 2) * 3;
            } else {
                for (let i = 0; i < nEndPoints - 1; i++) {
                    if (endPoints[i].x <= value && value <= endPoints[i + 1].x) {
                        index = i * 3;
                    }
                }
            }
        }

        // QC: ensure we've calculated a valid index
        const maxIndex = this.points.length - 4;
        return Math.min(Math.max(0, index), maxIndex);
    }

    /**
     * Ensure that the Bezier curve is not overlapping, as this will result in a NaN value when evaluating the curve
     * function.  Evaluate along the curve and return false if invalid values are found.
     *
     * @param increment Increment size for evaluation.  Lower increment will be more precise but require more evaluation.
     */
    _isDataValid(increment = 0.005): boolean {
        let x = 0;
        while (x < 1) {
            try {
                this._evalPercentage(x);
            } catch {
                return false;
            }
            x += increment;
        }
        return true;
    }

    _yValueAtX(index: number, x: number, counter = 0): number {
        const p0 = this._points[index] as EndPoint;
        // if X is to close to zero, represented here by MIN_X, bezier.intersects return a NaN. We bypass this here
        // by immediately returning the first value of Y.
        if (x < MIN_X) {
            return p0.y;
        }
        if (x < p0.x) {
            return p0.y;
        }

        const p3 = this._points[index + 3] as EndPoint;
        // if Y is to close to one, represented here by MAX_X, bezier.intersects return a NaN. We bypass this here by
        // immediately returning the last value of Y.
        if (x > MAX_X) {
            return p3.y;
        }
        if (x > p3.x) {
            return p3.y;
        }
        if (p0.x > p3.x) {
            throw Error(`Endpoints are swapped ${p0.x} > ${p3.x}`);
        }

        // return the last value when the curve is super compressed
        if (Math.abs(p0.x - p3.x) <= TOLERANCE) {
            return p3.y;
        }

        x = clamp(x, 0, 1);
        const p1 = this._points[index + 1];
        const p2 = this._points[index + 2];

        // While debugging I found a case where all Y values were the same which results in no animation but actually
        // causes an error. Here we make sure we escape it.
        if (p0.y === p1.y && p1.y === p2.y && p2.y === p3.y) {
            return p0.y;
        }

        const bezier = new Bezier([p0, p1, p2, p3]);
        const boundingBox = bezier.bbox();

        let intersections: (string | number)[] = [];
        if (x > p0.x - TOLERANCE && x < p0.x + TOLERANCE) {
            intersections = bezier.intersects({
                p1: { x: x + TOLERANCE, y: boundingBox.y.min - 1 },
                p2: { x: x + TOLERANCE, y: boundingBox.y.max + 1 },
            });
        } else if (x > p3.x - TOLERANCE && x < p3.x + TOLERANCE) {
            intersections = bezier.intersects({
                p1: { x: x - TOLERANCE, y: boundingBox.y.min - 1 },
                p2: { x: x - TOLERANCE, y: boundingBox.y.max + 1 },
            });
        } else {
            intersections = bezier.intersects({
                p1: { x, y: boundingBox.y.min - 1 },
                p2: { x, y: boundingBox.y.max + 1 },
            });
        }

        if (intersections.length === 1) {
            return bezier.get(Number(intersections[0])).y;
        }

        // Curve cleanup
        counter++;
        if (counter >= MAX_SCALE_DOWN_RECURSION) {
            throw Error(
                `Reached max recursion of ${MAX_SCALE_DOWN_RECURSION}. Result = ${JSON.stringify(this.getCurveDescription())}.`,
            );
        }
        // When the tangents are too long it results in a "s" curve which has multiple intersection points. We try
        // to correct this here by scaling down the tangents.
        p0.scaleOutTangent(SCALE_DOWN_FACTOR);
        p3.scaleInTangent(SCALE_DOWN_FACTOR);

        return this._yValueAtX(index, x, counter);
    }

    _evalPercentage(value: number) {
        // we need at least 2 points for any type of interpolation.
        if (this._points.length < 2) {
            return this._points[0].y;
        }
        const maxIndex = this.points.length - 4;
        const calIndex = this._getEndPointIndexForPercentage(value);
        const index = Math.min(Math.max(0, calIndex), maxIndex);

        if (index === undefined) {
            throw Error(`Invalid index ${index} for value ${value}.`);
        }
        return this._yValueAtX(index, value);
    }

    addStart(p1: Position, p2: Position, anchor: Anchor = Config.START_ANIMATION_CURVE_ANCHOR) {
        const ep1 = this._addEndPoint(p1);
        const cp2 = this._addControlPoint(p2);
        ep1.outPoint = cp2;
        ep1.anchor = anchor;
        cp2.endPoint = ep1;
        cp2.anchor = anchor;
    }

    addEnd(p1: Position, p2: Position, anchor: Anchor = Config.END_ANIMATION_CURVE_ANCHOR) {
        const cp2 = this._addControlPoint(p1);
        const ep1 = this._addEndPoint(p2);
        ep1.inPoint = cp2;
        ep1.anchor = anchor;
        cp2.endPoint = ep1;
        cp2.anchor = anchor;
    }

    addInbetween(
        p1: Position,
        p2: Position,
        p3: Position,
        anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR,
    ) {
        const cp1 = this._addControlPoint(p1);
        const ep1 = this._addEndPoint(p2);
        const cp2 = this._addControlPoint(p3);
        ep1.inPoint = cp1;
        ep1.outPoint = cp2;
        ep1.anchor = anchor;

        cp1.endPoint = ep1;
        cp1.anchor = anchor;

        cp2.endPoint = ep1;
        cp2.anchor = anchor;
    }

    override setCurveCoordinates(coordinatesString: string) {
        // re-init curve from SVG path string
        this.clearPoints();
        const tokens = coordinatesString.split(',');
        const coordinates = [];

        for (let i = 0; i < tokens.length; i++) {
            var coordinate;

            if (tokens[i].charAt(0) === 'C' || tokens[i].charAt(0) === 'M') {
                coordinate = parseFloat(tokens[i].slice(1, tokens[i].length));
            } else {
                coordinate = parseFloat(tokens[i]);
            }

            if (i % 2) {
                if (coordinates.length > 0) {
                    coordinates[coordinates.length - 1].y = coordinate;
                }
            } else {
                coordinates.push({ x: coordinate, y: 0 }); // Initialize y to avoid undefined
            }
        }
        this.addStart(coordinates[0], coordinates[1]);
        for (let i = 2; i < coordinates.length - 2; i += 3) {
            this.addInbetween(coordinates[i], coordinates[i + 1], coordinates[i + 2]);
        }
        this.addEnd(coordinates[coordinates.length - 2], coordinates[coordinates.length - 1]);
    }

    override getCurveCoordinates(precision = 5) {
        // create SVG path string from key array [{x:0, y:1}]
        let c = 'M';
        const points = this.getEndPoints();
        const np = points.length;

        for (let i = 0; i < np; i++) {
            if (i === 0) {
                c += `${parseFloat(points[i].x.toFixed(precision))},${parseFloat(points[i].y.toFixed(precision))},C`;
            } else {
                c += `${parseFloat(points[i].x.toFixed(precision))},${parseFloat(points[i].y.toFixed(precision))}`;
                if (i !== np - 1) {
                    c += ',';
                }
            }
        }

        return c;
    }

    override getCurveDescription(precision = 5): BezierCurveDescription {
        const desc: BezierCurveDescription = {
            duration: this.duration,
            classType: 'BezierCurve',
            points: [],
        };

        const endPoints = this.getEndPoints();
        for (let i = 0; i < endPoints.length; i++) {
            const config: PointDescription = {
                anchor: endPoints[i].anchor,
                position: endPoints[i].getPosition(precision),
            };

            if (endPoints[i].inPoint) {
                if (i !== 0) {
                    const d = this._getDistance(
                        endPoints[i].getPosition(),
                        endPoints[i].inPoint.getPosition(),
                    );
                    const a = this._getAngle(
                        endPoints[i].getPosition(),
                        endPoints[i].inPoint.getPosition(),
                    );
                    config.in = {
                        distance: parseFloat(d.toFixed(precision)),
                        angle: parseFloat(a.toFixed(precision)),
                    };
                }
            }
            if (endPoints[i].outPoint) {
                if (i !== endPoints.length - 1) {
                    const d = this._getDistance(
                        endPoints[i].getPosition(),
                        endPoints[i].outPoint.getPosition(),
                    );
                    const a = this._getAngle(
                        endPoints[i].getPosition(),
                        endPoints[i].outPoint.getPosition(),
                    );
                    config.out = {
                        distance: parseFloat(d.toFixed(precision)),
                        angle: parseFloat(a.toFixed(precision)),
                    };
                }
            }
            desc.points.push(config);
        }

        return desc;
    }

    /**
     * Extend the current curve with the given curve.
     */
    extend(curve: BezierCurve, anchor: Anchor = Config.END_ANIMATION_CURVE_ANCHOR) {
        // convert this end into an inbetween since we'll adding additional end points
        const nEndPoints = this.getEndPoints().length;
        const endPoint = this.getEndPoints()[nEndPoints - 1];
        const controlPoint = this._addControlPoint(endPoint.getPosition());
        endPoint.outPoint = controlPoint;
        controlPoint.anchor = endPoint.anchor;
        controlPoint.endPoint = endPoint;

        // add points from curve to this
        curve.getEndPoints().forEach((point, index) => {
            if (point.hasIn() && point.hasOut()) {
                this.addInbetween(
                    point.inPoint.getPosition(),
                    point.getPosition(),
                    point.outPoint.getPosition(),
                    anchor,
                );
            } else if (!point.hasIn() && point.hasOut()) {
                if (index === 0) {
                    this.addInbetween(
                        point.getPosition(),
                        point.getPosition(),
                        point.outPoint.getPosition(),
                        anchor,
                    );
                } else {
                    this.addStart(point.getPosition(), point.outPoint.getPosition(), anchor);
                }
            } else if (point.hasIn() && !point.hasOut()) {
                this.addEnd(point.inPoint.getPosition(), point.getPosition(), anchor);
            }
        });
    }

    /**
     * If the first point is not at x === 0, adds a point at x = 0. The end result is a flat 'delay' line segment
     * leading up to the original first point.
     */
    override addStartPoint() {
        if (this.points.length === 0) {
            return;
        }

        const firstPoint: EndPoint = this.points[0] as EndPoint;

        if (firstPoint.x === 0) {
            return;
        }

        // The base implementation adds an `EndPoint` but it does not know about control points.
        super.addStartPoint();

        const insertPoint: EndPoint = this.points[0] as EndPoint;
        const outPoint = this._createControlPoint(insertPoint);
        const inPoint = this._createControlPoint(firstPoint);

        insertPoint.outPoint = outPoint;
        outPoint.endPoint = insertPoint;
        outPoint.anchor = insertPoint.anchor;

        firstPoint.inPoint = inPoint;
        inPoint.endPoint = firstPoint;
        inPoint.anchor = firstPoint.anchor;

        this._points.splice(1, 0, outPoint);
        this._points.splice(2, 0, inPoint);
    }

    /**
     * If the last point is not at x === 1, adds a point at x = 1. The end result is a flat 'delay' line segment
     * trailing out of the original last point.
     */
    override addEndPoint() {
        if (this.points.length === 0) {
            return;
        }

        const prevPoint: EndPoint = this.points[this.points.length - 1] as EndPoint;

        if (prevPoint.x === 1) {
            return;
        }

        // The base implementation adds an `EndPoint` but it does not know about control points.
        super.addEndPoint();

        const nextPoint: EndPoint = this.points[this.points.length - 1] as EndPoint;
        const inPoint = this._createControlPoint(nextPoint);
        const outPoint = this._createControlPoint(prevPoint);

        nextPoint.inPoint = inPoint;
        inPoint.endPoint = nextPoint;
        inPoint.anchor = nextPoint.anchor;

        prevPoint.outPoint = outPoint;
        outPoint.endPoint = prevPoint;
        outPoint.anchor = prevPoint.anchor;

        this._points.splice(this.points.length - 1, 0, outPoint);
        this._points.splice(this.points.length - 1, 0, inPoint);
    }

    /**
     * Create a curve from the given interpolation preset configuration
     */
    static fromInterpolationPreset(config: InterpolationPreset): BezierCurve {
        assertObject(config);
        assertHasOwnProperties(config, ['delay', 'duration', 'interpolation']);
        assertInArray(config.interpolation, Config.ALL_INTERPOLATION_PRESETS);

        // how much space does the intro and outro each take up. This value can't be higher then 0.5
        const OCCUPATION_RATIO = 0.5;

        // split config into intro and outro
        // currently this is automatically mirrored since we don't have the ability to deal with separate
        // values for the intro (start) and outro (end).
        const intro = {
            delay: config.delay,
            duration: config.duration,
            interpolation: config.interpolation,
        };
        const outro = {
            delay: config.delay,
            duration: config.duration,
            interpolation: Config.MIRRORED_INTERPOLATION_PREFIX + config.interpolation,
        };

        // apply delay to intro
        const introPreset = Config.INTERPOLATION_PRESET_DATA[intro.interpolation];
        const introDuration = intro.delay + intro.duration;
        const introDelay = introDuration === 0 ? 0 : intro.delay / introDuration;
        const introCurve = BezierCurve.fromDescription(introPreset);
        introCurve.getEndPoints()[0].setDelta(introDelay, 0, true);
        introCurve.getEndPoints().forEach((endPoint) => {
            endPoint.anchor = Config.START_ANIMATION_CURVE_ANCHOR;
        });

        // scale to OCCUPATION_RATIO of the curve
        introCurve.scale(OCCUPATION_RATIO, true, Config.START_ANIMATION_CURVE_ANCHOR);

        // apply delay to outro
        const outroPreset = Config.INTERPOLATION_PRESET_DATA[outro.interpolation];
        const outroDuration = outro.delay + outro.duration;
        const outroDelay = outroDuration === 0 ? 0 : outro.delay / outroDuration;
        const outroCurve = BezierCurve.fromDescription(outroPreset);
        outroCurve.getEndPoints()[1].setDelta(-outroDelay, 0, true);
        outroCurve.getEndPoints().forEach((endPoint) => {
            endPoint.anchor = Config.END_ANIMATION_CURVE_ANCHOR;
        });

        // scale to OCCUPATION_RATIO of the curve
        outroCurve.scale(OCCUPATION_RATIO, true, Config.END_ANIMATION_CURVE_ANCHOR);

        // extend curve
        introCurve.extend(outroCurve);
        introCurve._duration = config.delay + config.duration;
        return introCurve;
    }

    /**
     * Sets up the point data for the curve.  The curve validation is based on sampling and therefore takes longer at
     * higher precision increments.  In order to accommodate both production and test requirements, a higher precision
     * can be provided here for running tests.
     */
    override setCurveDescription(data: BezierCurveDescription) {
        this.clearPoints();

        if (data.hasOwnProperty('duration')) {
            // directly set duration without scaling end points.
            this._duration = data.duration ?? this._duration;
        }

        const pointData = data.points;

        for (let i = 0; i < pointData.length; i++) {
            let anchor: Anchor = Config.MIDDLE_ANIMATION_CURVE_ANCHOR;
            if (pointData[i].hasOwnProperty('anchor') && pointData[i].anchor !== undefined) {
                anchor = pointData[i].anchor!;
            }

            const px = pointData[i].position.x;
            const py = pointData[i].position.y;
            const p = { x: px, y: py };

            let ip = null;
            if (pointData[i].hasOwnProperty('in') && pointData[i].in) {
                const inPoint = pointData[i].in!;
                ip = this._applyAngle(
                    {
                        x: inPoint.distance,
                        y: inPoint.distance,
                    },
                    inPoint.angle,
                );

                ip.x += px;
                ip.y += py;
            }
            let op = null;
            if (pointData[i].hasOwnProperty('out') && pointData[i].out) {
                const outPoint = pointData[i].out!;
                op = this._applyAngle(
                    {
                        x: outPoint.distance,
                        y: outPoint.distance,
                    },
                    outPoint.angle,
                );

                op.x += px;
                op.y += py;
            }

            if (ip && op) {
                this.addInbetween(ip, p, op, anchor);
            } else if (ip && !op) {
                this.addEnd(ip, p, anchor);
            } else if (!ip && op) {
                this.addStart(p, op, anchor);
            }
        }

        // try to automatically assign anchors when none are provided
        if (!this.hasBoundaryAnchors()) {
            this.assignBoundaryAnchors();
        }

        if (
            data.removeAnchoredPoints &&
            data.removeAnchoredPoints !== Config.NONE_ANIMATION_CURVE_ANCHOR
        ) {
            this.removePointsWithAnchor(data.removeAnchoredPoints);
        }
    }

    static fromDescription(description: BezierCurveDescription) {
        const instance = new BezierCurve();
        instance.setCurveDescription(description);
        return instance;
    }

    /**
     *    Creates a bezier curve from target values.  In.y and out.y are used as a y value additive for placing tangent points.
     *
     *    @param targets {Object} - An object describing the points and their in/out distance and offset.
     *                       [ {position: {x: 0, y: 0}, out: {distance: 0, y: 0}},
     *                         {position: {x: 0.2, y: 0}, in: {distance: 0.05, y: 0}, out: {distance: 0.05, y: 0}},
     *                         {position: {x: 1, y: 1}, in: {distance: 0, y: 0}} ]
     *    @param duration - The overall duration of the subSlide, added to the returned description
     **/
    static descriptionFromTargetValues(
        targets: {
            anchor?: Anchor;
            position: Position;
            in?: {
                distance: number;
                // TODO: figure out why here we use distance and y instead of distance and angle
                y: number;
            };
            out?: {
                distance: number;
                // TODO: figure out why here we use distance and y instead of distance and angle
                y: number;
            };
        }[],
        duration: number,
    ) {
        const minValue = targets.reduce(
            (min, target) => (target.position.y < min ? target.position.y : min),
            targets[0].position.y,
        );
        const maxValue = targets.reduce(
            (max, target) => (target.position.y > max ? target.position.y : max),
            targets[0].position.y,
        );
        const nTargets = targets.length;
        const instance = new BezierCurve();
        targets.forEach((target, i) => {
            const x = target.position.x;
            const y = target.position.y;

            if (i === 0 && target.out) {
                instance.addStart(
                    { x, y },
                    { x: x + target.out.distance, y: y },
                    Config.START_ANIMATION_CURVE_ANCHOR,
                );
            } else if (i === nTargets - 1 && target.in) {
                instance.addEnd(
                    { x: x - target.in.distance, y: y + target.in.y },
                    { x, y },
                    Config.END_ANIMATION_CURVE_ANCHOR,
                );
            } else if (target.in && target.out) {
                instance.addInbetween(
                    { x: x - target.in.distance, y: y + target.in.y },
                    { x, y },
                    { x: x + target.out.distance, y: y + target.out.y },
                );
            }
        });
        const description = instance.getCurveDescription();
        if (duration !== undefined) {
            description.duration = duration;
        }
        return {
            curve: description,
            drivenRange: [minValue, maxValue],
            curveInstance: instance,
        };
    }
}
